// src/components/Dashboard/ProjectDetailsView.tsx
/**
 * ProjectDetailsView - Full-width custom fields panel
 * Renders when the Details tab is active in the project dashboard.
 * Shows CustomFields with all layers, Security Groups, and Layers.
 */

import React, { useMemo, useEffect, useState } from 'react';
import CustomFields from '../CustomFields/CustomFields';
import type { ProjectInfo } from '../../types/dashboard';
import { authStorage } from '../../lib/storage';
import { config } from '../../config/env';
import ConversationApi from '../ConversationItem/api/ConversationApi';
import { useConversationBus } from '../../hooks/useConversationBus';
import './ProjectDetailsView.css';

interface ProjectDetailsViewProps {
  projectInfo: ProjectInfo;
  projectId: number;
}

export const ProjectDetailsView: React.FC<ProjectDetailsViewProps> = ({
  projectInfo,
  projectId,
}) =>
{
  const [fullConversation, setFullConversation] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Build the config for CustomFields
  const customFieldsConfig = useMemo(() =>
  {
    const token = authStorage.getAccessToken() || '';
    let apiUrl = config.api.baseUrl;

    // Ensure trailing slash — callFetch concatenates url + endpoint directly
    if (!apiUrl.endsWith('/'))
    {
      apiUrl += '/';
    }

    return {
      apiUrl,
      authToken: token,
      canEdit: true,
      isNew: false,
      showHeader: true,
      callApi: true,
      disableAutoSave: false,
      expandAll: true,
      isConversationDetailsView: true,
    };
  }, []);

  useEffect(() =>
  {
    let isMounted = true;
    const fetchFullDetails = async () =>
    {
      if (!projectInfo?.ConversationId) return;
      setIsLoading(true);
      try
      {
        const apiConfigForFetch = {
          ...customFieldsConfig,
          url: customFieldsConfig.apiUrl,
          token: customFieldsConfig.authToken,
        };
        const response = await ConversationApi.getConversationById(apiConfigForFetch as any, {
          conversationId: projectInfo.ConversationId,
          layersToHide: '',
          getAllReplies: false,
          isConversationDetailsView: true,
          totalReplies: 0,
          isEnabled: true,
        });

        if (isMounted && response)
        {
          setFullConversation(response);
        }
      }
      catch (error)
      {
        console.error('Error fetching full project conversation details:', error);
      }
      finally
      {
        if (isMounted)
        {
          setIsLoading(false);
        }
      }
    };

    fetchFullDetails();

    return () =>
    {
      isMounted = false;
    };
  }, [projectInfo?.ConversationId, customFieldsConfig]);

  useConversationBus('conversation-updated', projectInfo?.ConversationId, data =>
  {
    setFullConversation((prev: any) => ({ ...prev, ...data.updates }));
  });

  useConversationBus('image-updated', projectInfo?.ConversationId, data =>
  {
    setFullConversation((prev: any) =>
    {
      if (!prev) return prev;

      const updated = { ...prev };
      const currentLayers =
        updated.LayerCustomFields ||
        updated.layerCustomFields ||
        projectInfo.LayerCustomFields ||
        [];
      const layers = [...currentLayers];

      const layerZeroIdx = layers.findIndex((l: any) => l.LayerId === 0);

      if (layerZeroIdx >= 0)
      {
        const layer = { ...layers[layerZeroIdx] };
        if (layer.CustomFields)
        {
          layer.CustomFields = layer.CustomFields.map((f: any) =>
            f.Description === 'Photo' ? { ...f, binary: data.imageUrl } : f,
          );
          layers[layerZeroIdx] = layer;
          updated.LayerCustomFields = layers;
          // For CustomFields to re-render, we might need layerCustomFields too
          updated.layerCustomFields = layers;
        }
      }
      // Ensure fullConversation gets a new reference at the top tier
      return { ...updated };
    });
  });

  // Build the conversation item that CustomFields expects
  const customFieldsItem = useMemo(() =>
  {
    const sourceData = fullConversation || projectInfo;

    return {
      ...sourceData,
      ConversationId: projectInfo.ConversationId,
      ProjectId: projectId,
      ConfigValueId: projectInfo.ConfigValueID,
      IsProjectConversation: true,
      IsEnable: true,
      LayerCustomFields: (sourceData.LayerCustomFields ||
        sourceData.layerCustomFields ||
        projectInfo.LayerCustomFields ||
        []) as any[],
      ConversationLayers: (sourceData.ConversationLayers || []).map((layer: any) => ({
        ...layer,
        Name: layer.LayerName || layer.Name,
        Color: layer.LayerColor || layer.Color,
      })),
      ConversationGroups: (sourceData.ConversationGroups || []).map((group: any) => ({
        ...group,
        Name: group.RoleName || group.Name,
        GroupLayers: (sourceData.ConversationLayers || [])
          .filter(
            (layer: any) =>
              layer.GroupId === group.ConversationGroupId || layer.GroupId === group.Id,
          )
          .map((layer: any) => ({
            ...layer,
            Name: layer.LayerName || layer.Name,
            Color: layer.LayerColor || layer.Color,
          })),
      })),
    };
  }, [projectInfo, fullConversation, projectId]);

  if (isLoading && !fullConversation)
  {
    return (
      <div className="project-details-view">
        <div className="project-details__skeleton">
          <div className="project-details__skeleton-header"></div>
          <div className="project-details__skeleton-row">
            <div className="project-details__skeleton-field"></div>
            <div className="project-details__skeleton-field"></div>
            <div className="project-details__skeleton-field"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="project-details-view">
      <CustomFields
        key={`project-details-${projectId}`}
        config={customFieldsConfig}
        item={customFieldsItem as any}
        configValueId={projectInfo.ConfigValueID}
        projectId={projectId}
        isProjectView
      />
    </div>
  );
};

export default ProjectDetailsView;
